import type { Project, SocialLink, Bio, ClientLogo, Tool } from '@/types'

export interface Education {
  degree: string
  school: string
  date: string
}

export const projects: Project[] = [
  {
    slug: 'verizon',
    name: 'Verizon',
    homepageName: 'Verizon',
    tags: ['Product Design', 'Prototyping', 'Brand'],
    homepageTags: 'Product motion guidelines, transitions, component logic, cross-platform handoff',
    date: '2021 - 2022',
    role: 'Senior Motion Designer',
    description:
      'At W12 Studios, I partnered with Verizon to define and scale motion across their product ecosystem, integrating it into the design system as a functional layer rather than surface decoration. This included developing structured motion guidelines and translatable UI templates that improved consistency, streamlined collaboration between design and engineering, and made implementation more efficient. A clear motion framework - covering timing, easing, and behavioral principles - was validated through user testing and strengthened the overall product experience.',
    videoIds: ['903290794', '903291154'],
    previewVideoId: '903290794',
    thumbnailGradient: 'linear-gradient(135deg, #E8734A 0%, #CD201F 100%)',
    challenge:
      'Verizon\'s digital product team lacked a unified motion language, resulting in inconsistent transitions and animations across platforms that slowed design iterations and confused the developer handoff process.',
    solution:
      'I designed and documented a comprehensive motion guideline system with reusable Lottie-based templates, standardized easing curves, and component-level animation specs that both designers and developers could reference.',
    techStack: ['After Effects', 'Principle', 'Figma', 'Lottie', 'Adobe Creative Suite'],
    metrics: [
      { value: '15%', label: 'Faster design iterations' },
      { value: '30%', label: 'Reduction in developer onboarding time' },
      { value: '10%', label: 'Increase in user satisfaction during testing' },
    ],
  },
  {
    slug: 'bumble-badoo',
    name: 'Bumble - Badoo',
    homepageName: 'Bumble & Badoo',
    tags: ['Brand', 'Growth Marketing', 'Product'],
    homepageTags: 'Campaign storytelling, social-first motion, Piccadilly Circus feature',
    date: '2019 - 2021',
    role: 'Motion Designer',
    description:
      'At Bumble and Badoo, I worked across global brand, product, and performance marketing, delivering high-volume motion and video production across multiple international markets. I contributed to the creation of 100+ localized social assets per month across 7 markets, supporting UA and ASO production from concept through iteration and localization. I also collaborated with product teams on feature launches: translating product updates into clear, engaging marketing narratives across social, paid, and in-app touchpoints. My work connected brand, product, and performance - turning campaign ideas into scalable motion systems built to drive results. The focus was always on clarity, storytelling, and production at scale across international markets.',
    videoIds: ['903292039'],
    previewVideoId: '903292039',
    thumbnailGradient: 'linear-gradient(135deg, #FD6B6B 0%, #FFD93D 100%)',
    challenge:
      'Bumble and Badoo needed high-performing video content that could drive user acquisition across 7+ international markets while maintaining distinct brand identities for each app.',
    solution:
      'I developed a scalable motion production pipeline for campaign storytelling and social-first content, creating adaptable templates that could be localized efficiently while preserving each brand\'s visual DNA.',
    techStack: ['After Effects', 'Premiere Pro', 'Illustrator', 'Figma', 'Adobe Creative Suite'],
    metrics: [
      { value: '7+', label: 'Global markets reached' },
      { value: 'Piccadilly Circus', label: 'Featured placement' },
      { value: 'EMEA', label: 'Regional campaign coverage' },
    ],
  },
  {
    slug: 'vodafone',
    name: 'Vodafone Motion System',
    homepageName: 'Vodafone',
    tags: ['Product', 'Motion Systems', 'Design Tokens'],
    homepageTags:
      'First in-house motion designer, motion system, custom easing, animation tokens',
    date: '2022 - 2025',
    role: 'Lead Motion Designer',
    description:
      "Hired as Vodafone's first in-house Motion Designer, I built their complete motion design system from scratch – 8 motion principles, custom easing curves, a structured timing framework, JSON animation tokens, and a component library – unifying motion across web, iOS, and Android. I led motion design for 5+ applications, delivering Motion User Interface specs, Motion Developer Guides, and interactive prototypes to engineering squads across the organisation.",
    videoIds: ['903292611'],
    previewVideoId: '903292611',
    thumbnailGradient: 'linear-gradient(135deg, #E60000 0%, #990000 100%)',
    challenge:
      "Vodafone had no in-house motion capability. Animations were outsourced to agencies, resulting in inconsistent, overly bouncy animations that confused users, slow transitions correlated with funnel drop-offs, no accessibility controls for motion-sensitive users, and ambiguous engineering handoff that slowed agile delivery.",
    solution:
      "I architected a scalable, physics-based motion framework from the ground up – defining 8 foundational principles, engineering custom cubic bezier curves, building a structured timing framework, and translating all kinetic values into robust design tokens for iOS, Android, and Web platforms.",
    techStack: ['After Effects', 'Lottie', 'Figma', 'Principle', 'ProtoPie', 'Adobe Creative Suite'],
    metrics: [
      { value: '10%', label: 'Increase in client conversion' },
      { value: '60fps', label: 'Performance target across all device tiers' },
      { value: '5+', label: 'Applications unified under one motion system' },
    ],
  },
  {
    slug: 'tobi',
    name: 'TOBi AI Assistant',
    homepageName: 'TOBi',
    tags: ['AI', 'Character Animation', 'Lottie'],
    homepageTags: '28 emotions, faux-3D rigging, Lottie JSON, contextual triggers',
    date: '2022 - 2025',
    role: 'Lead Motion Designer',
    description:
      "Designed and animated TOBi, Vodafone's conversational AI assistant, transitioning it from a static chatbot avatar into a highly dynamic, animated entity. Rigged the character in After Effects to achieve a faux-3D aesthetic with 28 distinct emotional states, exported as lightweight Lottie JSON files for seamless cross-platform deployment.",
    videoIds: ['903316776', '380206784', '380206771', '380206812', '380206795'],
    previewVideoId: '903316776',
    thumbnailGradient: 'linear-gradient(135deg, #FF6B6B 0%, #E60000 100%)',
    challenge:
      "TOBi existed as a static chatbot avatar with no animation or personality. The hypothesis was that introducing fluid, performant animation would make the assistant more expressive and engaging – boosting conversation initiation rates and building user trust.",
    solution:
      "I rigged the flat asset in After Effects for faux-3D motion, engineered 28 distinct emotion states as seamless 2 or 4-second loops, and exported everything as lightweight Lottie JSON. Animations are triggered programmatically by UI events and real-time chat sentiment analysis.",
    techStack: ['After Effects', 'Lottie', 'Figma', 'Cinema 4D', 'Adobe Creative Suite'],
    metrics: [
      { value: '9%', label: 'Increase in conversation engagement' },
      { value: '10%', label: 'Lift in task completion rates' },
      { value: '28', label: 'Distinct emotional states' },
    ],
  },
  {
    slug: 'nielsen',
    name: 'Nielsen',
    homepageName: 'Nielsen',
    tags: ['Brand Identity', 'Product'],
    homepageTags: 'Narrative motion, product explainers, editorial-style promos',
    date: '2022',
    role: 'Motion Designer',
    description:
      "I contributed to Nielsen's rebranding by leading the motion direction across digital products. Working closely with visual designers and prototyping teams, I developed motion guidelines that strengthened usability and accelerated design iterations.",
    videoIds: ['903295427'],
    previewVideoId: '903295427',
    thumbnailGradient: 'linear-gradient(135deg, #2D6DF6 0%, #00C389 100%)',
    challenge:
      'Nielsen\'s product team needed motion guidelines that could improve usability and create a consistent animation language across their UI, while being easy for developers to implement.',
    solution:
      'I created translatable motion templates and guidelines that standardized animation behavior across Nielsen\'s product interfaces, focusing on usability-first motion that guided users through complex data interactions.',
    techStack: ['After Effects', 'Figma', 'Lottie', 'Illustrator', 'Adobe Creative Suite'],
    metrics: [
      { value: '20%', label: 'Faster design iterations' },
      { value: '15%', label: 'Reduction in developer onboarding time' },
    ],
  },
  {
    slug: 'hbo-discovery',
    name: 'HBO Max / Discovery',
    homepageName: 'HBO Max',
    tags: ['Broadcast', 'Motion Graphics', 'Editorial'],
    homepageTags: 'On-screen UI graphics, social media simulations, title treatments',
    date: '2025 - 2026',
    role: 'Lead Motion Designer (Consultant)',
    description:
      'Designed and animated on-screen UI graphics for the hit docuseries Deadly Influence: The Social Media Murders on Investigation Discovery / HBO Max. Created realistic, animated social media simulations (TikTok/Instagram interfaces) and title treatments for 6 episodes, ensuring broadcast-safe standards.',
    videoIds: [],
    previewVideoId: '',
    youtubeVideoId: 'EAlm4iupmss',
    thumbnailGradient: 'linear-gradient(135deg, #7B2FF7 0%, #2D1B69 100%)',
    thumbnailImage: '/projects/hbo-deadly-influence-backdrop.jpg',
    challenge:
      'The docuseries needed realistic, animated recreations of social media interfaces (TikTok, Instagram) that would feel authentic on screen while meeting strict broadcast-safe standards across 6 episodes.',
    solution:
      'I designed and animated pixel-perfect social media UI simulations and title treatments that seamlessly integrated into the editorial narrative, maintaining visual consistency across all episodes.',
    techStack: ['After Effects', 'Illustrator', 'Premiere Pro', 'Adobe Creative Suite'],
    metrics: [
      { value: '6', label: 'Episodes with custom motion graphics' },
    ],
  },
]

export const socialLinks: SocialLink[] = [
  {
    platform: 'linkedin',
    url: 'https://www.linkedin.com/in/nataliatarasova-motion/',
    label: 'LinkedIn',
  },
  {
    platform: 'instagram',
    url: 'https://www.instagram.com/video.ergo.sum/',
    label: 'Instagram',
  },
]

export const bio: Bio = {
  name: 'Natalia Tarasova',
  role: 'Lead Motion Designer',
  headline:
    'Motion Designer crafting scalable systems for global brands.',
  summary:
    'A forward-thinking content creator and motion designer, recently relocated from London to New York, with a background in digital marketing, product, and communications across global brands. Based in Brooklyn, I bring excellent design, photography, and videography skills and a keen eye for integrating current digital trends into brand strategies. Proven ability to lead design language initiatives in cross-functional teams across product, engineering, and UX. Authorised to work in the U.S.',
  email: 'hello@nataliatarasova.info',
  resumeUrl: '/natalia-tarasova-resume.pdf?v=3',
  availableForWork: true,
}

export const showreelVideoId = '903292039'

export const clients: ClientLogo[] = [
  { name: 'HBO', src: '/clients/hbo.svg' },
  { name: 'Vodafone', src: '/clients/vodafone.svg' },
  { name: 'Verizon', src: '/clients/verizon.svg' },
  { name: 'Bumble', src: '/clients/bumble.svg' },
  { name: 'Badoo', src: '/clients/badoo.svg' },
  { name: 'Nielsen', src: '/clients/nielsen.svg' },
]

export const tools: Tool[] = [
  { name: 'After Effects', category: 'motion' },
  { name: 'Cinema 4D', category: '3d' },
  { name: 'Figma', category: 'design' },
  { name: 'Principle', category: 'design' },
  { name: 'ProtoPie', category: 'design' },
  { name: 'Framer', category: 'design' },
  { name: 'Lottie', category: 'development' },
  { name: 'Rive', category: 'development' },
  { name: 'Animation Tokens', category: 'development' },
  { name: 'Git', category: 'development' },
  { name: 'Adobe Creative Suite', category: 'design' },
]

export const education: Education[] = [
  {
    degree: 'User Experience Design',
    school: 'University of the Arts London',
    date: '2020 - 2021',
  },
  {
    degree: 'MA History of Art',
    school: 'Institute of History of Cultures, Moscow',
    date: '2007 - 2013',
  },
]

export function getProjectBySlug(slug: string): Project | undefined {
  return projects.find((p) => p.slug.toLowerCase() === slug.toLowerCase())
}

export function getAdjacentProjects(slug: string): {
  prev: Project | undefined
  next: Project | undefined
} {
  const index = projects.findIndex((p) => p.slug === slug)
  return {
    prev: index > 0 ? projects[index - 1] : undefined,
    next: index < projects.length - 1 ? projects[index + 1] : undefined,
  }
}
